// IND-046: «Editar fila» completa: material, proveedor y cantidades del mes con
// sus albaranes. Las sugerencias solo se escriben con «Usar».
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { getRecountEstimates, getRecountRowDetail } from '../services/industrialCostService';
import type { RecountRowSource, RecountSheetRow } from '../services/industrialCostService';
import { esNumberFormat } from '../utils/numberFormat';
import { fmtQty, isSupplierCode, isTyped } from '../utils/recountSheet';
import { kgDraft, MONTH_NAMES, parseKg } from '../utils/wasteControl';

const MUTED = '#94a3b8';
const AMBER = '#fde68a';
const BLUE = '#67e8f9';
const SECTION = { fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: '#7dd3fc' };
const BOX = { p: 1.25, borderRadius: 3, bgcolor: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.16)' };
const BUTTON = { textTransform: 'none', fontWeight: 800 };
const price = esNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 4 });
const suggestedPrice = esNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
const euros = esNumberFormat({ maximumFractionDigits: 0 });
const whole = esNumberFormat({ maximumFractionDigits: 0 });

type TextKey = 'material_code' | 'reference' | 'destination' | 'class_label' | 'supplier';
type NumberKey = 'share' | 'previous_qty' | 'current_qty' | 'purchases_qty' | 'price';
type FormKey = TextKey | NumberKey | 'unit';
type SuggestedKey = 'purchases_qty' | 'price';

export type RecountRowSave = {
  changes: [string, number | string | null, RecountRowSource][];
  code: { supplier: string; value: string | null } | null;
};

function shortDay(value: string): string {
  const [, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}`;
}

function fmtPrice(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : price.format(value);
}

function shareDraft(value: number | null): string {
  return value === null ? '' : kgDraft(Math.round(value * 1_000_000) / 10_000);
}

function initialForm(row: RecountSheetRow): Record<FormKey, string> {
  return {
    material_code: row.material_code,
    reference: row.reference ?? '',
    destination: row.destination ?? '',
    class_label: row.class_label ?? '',
    supplier: row.supplier ?? '',
    share: shareDraft(row.share),
    previous_qty: kgDraft(row.previous_qty),
    current_qty: kgDraft(row.current_qty),
    purchases_qty: kgDraft(row.purchases_qty),
    price: kgDraft(row.price),
    unit: row.unit,
  };
}

export default function IndustrialCostRecountRowDialog({
  row,
  number,
  total,
  year,
  month,
  monthName,
  previousDay,
  currentDay,
  canEditCode,
  busy,
  failed,
  hasPrevious: canGoBack,
  hasNext,
  onMove,
  onClose,
  onSave,
  onDelete,
}: {
  row: RecountSheetRow;
  number: number;
  total: number;
  year: number;
  month: number;
  monthName: string;
  previousDay: string;
  currentDay: string;
  canEditCode: boolean;
  busy: boolean;
  failed: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  onMove: (step: number) => void;
  onClose: () => void;
  onSave: (save: RecountRowSave, goNext: boolean) => void;
  onDelete: () => void;
}) {
  // Lo que había al abrir: una relectura de la hoja no cuenta como cambio.
  const [initial] = useState(() => initialForm(row));
  const [initialCode] = useState(row.supplier_code ?? '');
  const [form, setForm] = useState(initial);
  const [code, setCode] = useState(initialCode);
  // Lo que llega con «Usar» queda como sugerencia, no como tecleado.
  const used = useRef<Partial<Record<SuggestedKey, boolean>>>({});

  const detailQuery = useQuery({
    queryKey: ['industrial-cost-recount-row-detail', row.id, row.supplier_code],
    queryFn: () => getRecountRowDetail(row.id),
  });
  const estimatesQuery = useQuery({
    queryKey: ['industrial-cost-recount-estimates', year, month],
    queryFn: () => getRecountEstimates(year, month),
  });
  const detail = detailQuery.data;

  const set = (key: FormKey, value: string) => {
    if (key === 'purchases_qty' || key === 'price') used.current[key] = false;
    setForm((current) => ({ ...current, [key]: value }));
  };
  const useSuggestion = (key: SuggestedKey, value: number) => {
    setForm((current) => ({ ...current, [key]: kgDraft(value) }));
    used.current[key] = true;
  };
  const numberError = (key: NumberKey): boolean => {
    const value = parseKg(form[key]);
    if (value === null) return false;
    if (!Number.isFinite(value)) return true;
    if (key === 'share') return value < 0 || value > 100;
    if (key === 'price') return value < 0 || value > 1_000_000;
    return Math.abs(value) > 1_000_000_000 || (key !== 'purchases_qty' && value < 0);
  };
  const codeText = code.trim();
  const codeError = codeText !== '' && !isSupplierCode(codeText);
  const numbers: NumberKey[] = ['share', 'previous_qty', 'current_qty', 'purchases_qty', 'price'];
  const texts: TextKey[] = row.added_by_hand
    ? ['material_code', 'reference', 'destination', 'class_label', 'supplier']
    : ['reference', 'destination', 'class_label', 'supplier'];
  const changed = (key: FormKey) => form[key].trim() !== initial[key];
  const invalid = numbers.some(numberError) || codeError || (row.added_by_hand && !form.material_code.trim());
  const supplierName = form.supplier.trim();
  const codeChanged = codeText !== initialCode;
  const dirty = [...texts, ...numbers, 'unit' as const].some(changed) || codeChanged;

  const collect = (): RecountRowSave => {
    const source = (key: FormKey): RecountRowSource =>
      (key === 'purchases_qty' || key === 'price') && used.current[key] ? 'sugerencia' : 'tecleado';
    const number = (key: NumberKey) => {
      const value = parseKg(form[key]);
      return key === 'share' && value !== null ? value / 100 : value;
    };
    return {
      changes: [
        ...texts.filter(changed).map((key): RecountRowSave['changes'][number] => [key, form[key].trim() || null, 'tecleado']),
        ...(changed('unit') ? [['unit', form.unit, 'tecleado'] as RecountRowSave['changes'][number]] : []),
        ...numbers.filter(changed).map((key): RecountRowSave['changes'][number] => [key, number(key), source(key)]),
      ],
      code: codeChanged && supplierName ? { supplier: supplierName, value: codeText || null } : null,
    };
  };

  const amber = (key: FormKey) =>
    !((key === 'purchases_qty' || key === 'price') && used.current[key]) && (isTyped(row, key) || changed(key));
  const field = (key: FormKey, label: string, disabled = false): ReactNode => (
    <TextField
      size="small"
      label={label}
      value={form[key]}
      disabled={disabled}
      onChange={(event) => set(key, event.target.value)}
      fullWidth
      sx={amber(key) ? { '& .MuiInputBase-input': { color: AMBER } } : undefined}
    />
  );

  const current = parseKg(form.current_qty);
  const previous = parseKg(form.previous_qty);
  const purchases = parseKg(form.purchases_qty);
  const rowPrice = parseKg(form.price);
  // IND-053: la silicona sin MES ANTERIOR no tiene consumo («—»).
  const real = current !== null && Number.isFinite(current) && !(row.table_kind === 'single' && previous === null)
    ? (previous ?? 0) + (purchases ?? 0) - current
    : null;
  const theoretical = estimatesQuery.data?.[row.id]?.consumption;
  const difference = real !== null && theoretical !== undefined ? real - theoretical : null;
  const value = current !== null && Number.isFinite(current) ? current * (rowPrice ?? 0) : null;
  const unit = form.unit === 'unidades' ? 'ud' : 'kg';

  const prices = detail?.previous_prices ?? [];
  const pricesText = prices.some((item) => item.price !== null)
    ? prices.map((item) => fmtPrice(item.price)).join(' · ')
    : 'sin datos';
  const pricesTitle = prices.map((item) => `${MONTH_NAMES[item.month - 1]} ${item.year}`).join(' · ');

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-label="Editar fila"
      PaperProps={{ sx: { width: 'min(1120px, calc(100% - 32px))', m: 2, borderRadius: 4, bgcolor: '#131c2e', backgroundImage: 'none' } }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(148,163,184,0.16)' }}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, color: MUTED }}>{`Fila ${number} de ${total} · ${monthName} ${year}`}</Typography>
          <Typography noWrap sx={{ fontSize: 20, fontWeight: 800, color: '#f8fafc' }}>
            {[row.material_code, row.reference].filter(Boolean).join(' · ')}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              component="span"
              sx={{
                fontSize: 12, fontWeight: 800, px: 1, borderRadius: 1.5,
                bgcolor: row.supplier_code ? 'rgba(34,211,238,0.16)' : 'rgba(251,191,36,0.18)',
                color: row.supplier_code ? BLUE : AMBER,
              }}
            >
              {row.supplier_code ?? 'sin código'}
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{row.supplier ?? '—'}</Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>{`· ${[row.destination, unit].filter(Boolean).join(' · ')}`}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            disabled={!canGoBack || dirty || busy}
            title={dirty ? 'Guarda o cancela antes de cambiar de fila' : undefined}
            onClick={() => onMove(-1)}
            sx={{ textTransform: 'none' }}
          >
            ‹ Anterior
          </Button>
          <Button
            variant="outlined"
            disabled={!hasNext || dirty || busy}
            title={dirty ? 'Guarda o cancela antes de cambiar de fila' : undefined}
            onClick={() => onMove(1)}
            sx={{ textTransform: 'none' }}
          >
            Siguiente ›
          </Button>
          <IconButton aria-label="Cerrar" onClick={onClose} sx={{ color: MUTED }}>×</IconButton>
        </Stack>
      </Stack>

      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '300px minmax(0, 1fr)' } }}>
        <Stack spacing={2} sx={{ p: 2.25, borderRight: { md: '1px solid rgba(148,163,184,0.16)' } }}>
          <Stack spacing={1.25} component="section">
            <Typography component="h3" sx={SECTION}>1 · MATERIAL</Typography>
            {field('material_code', 'Código', !row.added_by_hand)}
            {field('reference', 'Referencia')}
            {field('destination', 'Destino')}
            {field('class_label', 'Clase')}
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="% DESTINO 1"
                value={form.share}
                error={numberError('share')}
                onChange={(event) => set('share', event.target.value)}
                InputProps={{ endAdornment: <Typography sx={{ color: MUTED, fontSize: 13 }}>%</Typography> }}
                sx={amber('share') ? { '& .MuiInputBase-input': { color: AMBER } } : undefined}
              />
              <TextField
                select
                size="small"
                label="Unidad"
                value={form.unit}
                onChange={(event) => set('unit', event.target.value)}
                sx={{ minWidth: 110 }}
              >
                <MenuItem value="kg">kg</MenuItem>
                <MenuItem value="unidades">ud</MenuItem>
              </TextField>
            </Stack>
          </Stack>
          <Stack spacing={1.25} component="section">
            <Typography component="h3" sx={SECTION}>2 · PROVEEDOR</Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="Código"
                value={code}
                disabled={!canEditCode || !supplierName}
                error={codeError}
                inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                onChange={(event) => setCode(event.target.value)}
                sx={{ width: 104, flexShrink: 0, ...(codeChanged ? { '& .MuiInputBase-input': { color: AMBER } } : {}) }}
              />
              {field('supplier', 'Nombre en el recuento')}
            </Stack>
            <Typography sx={{ fontSize: 12, color: codeError ? '#fca5a5' : MUTED }}>
              {codeError ? 'Solo números, de 1 a 5 cifras.' : 'El código vale para todas las filas de este proveedor.'}
            </Typography>
          </Stack>
        </Stack>

        <Stack spacing={1.5} sx={{ p: 2.25, minWidth: 0 }}>
          <Typography component="h3" sx={SECTION}>3 · CANTIDADES Y PRECIO DEL MES</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25 }}>
            <Card label={`Mes anterior (${previousDay})`} hint="Del recuento anterior">
              <CardInput value={form.previous_qty} amber={amber('previous_qty')} error={numberError('previous_qty')} onChange={(text) => set('previous_qty', text)} label="Mes anterior" />
            </Card>
            <Card label={`Actual (${currentDay})`} hint="Lo contado" accent>
              <CardInput value={form.current_qty} amber={amber('current_qty')} error={numberError('current_qty')} onChange={(text) => set('current_qty', text)} label="Actual" />
            </Card>
            <Card
              label={`Compras ${unit}`}
              hint="Suma de albaranes del mes"
              suggestion={row.purchases_suggested !== null && row.purchases_suggested !== parseKg(form.purchases_qty) ? (
                <Suggestion text={fmtQty(row.purchases_suggested, unit)} onUse={() => useSuggestion('purchases_qty', row.purchases_suggested as number)} />
              ) : null}
            >
              <CardInput value={form.purchases_qty} amber={amber('purchases_qty')} error={numberError('purchases_qty')} onChange={(text) => set('purchases_qty', text)} label="Compras" />
            </Card>
            <Card
              label={`Precio €/${unit}`}
              hint={detail?.last_price_date ? `Último albarán (${shortDay(detail.last_price_date)})` : 'Sin albarán'}
              suggestion={row.price_suggested !== null && row.price_suggested !== parseKg(form.price) ? (
                <Suggestion text={suggestedPrice.format(row.price_suggested)} onUse={() => useSuggestion('price', row.price_suggested as number)} />
              ) : null}
            >
              <CardInput value={form.price} amber={amber('price')} error={numberError('price')} onChange={(text) => set('price', text)} label="Precio" />
            </Card>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.2fr) minmax(0, 1fr)' }, gap: 1.5 }}>
            <Stack spacing={0.75} component="section" sx={{ ...BOX, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#f8fafc' }}>
                {`Albaranes de ${monthName} · ${row.supplier_code || 'sin código'} · ${row.material_code}`}
              </Typography>
              {!row.supplier_code ? (
                <Typography sx={{ fontSize: 12, color: MUTED }}>Sin código: sin sugerencia.</Typography>
              ) : detailQuery.isError ? (
                <Typography sx={{ fontSize: 12, color: '#fca5a5' }}>No se pudieron leer los albaranes.</Typography>
              ) : !detail ? (
                <Typography sx={{ fontSize: 12, color: MUTED }}>Leyendo albaranes…</Typography>
              ) : !detail.lines.length ? (
                <Typography sx={{ fontSize: 12, color: MUTED }}>Ningún albarán este mes.</Typography>
              ) : (
                <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                  <Table size="small" sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { px: 0.75, py: 0.5, fontSize: 12, borderColor: 'rgba(148,163,184,0.10)' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 58 }}>Fecha</TableCell>
                        <TableCell>Albarán</TableCell>
                        <TableCell align="right" sx={{ width: 92 }}>Cantidad</TableCell>
                        <TableCell align="right" sx={{ width: 76 }}>{`Precio €/${unit}`}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.lines.map((line, index) => (
                        <TableRow key={`${line.date}|${line.document}|${index}`}>
                          <TableCell>{shortDay(line.date)}</TableCell>
                          <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={line.document ?? undefined}>{line.document ?? '—'}</TableCell>
                          <TableCell align="right" sx={{ color: line.quantity < 0 ? '#fca5a5' : undefined }}>{fmtQty(line.quantity, unit)}</TableCell>
                          <TableCell align="right">{fmtPrice(line.price)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total del mes · último precio</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: BLUE }}>{fmtQty(detail.total, unit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: BLUE }}>{fmtPrice(detail.last_price)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>

            <Stack spacing={0.75} component="section" sx={{ ...BOX, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#f8fafc' }}>Consumo del mes</Typography>
              <Line label="Real (anterior + compras − actual)" value={fmtQty(real, unit)} strong />
              <Line
                label="Teórico de los partes (orientativo)"
                value={estimatesQuery.isLoading ? '…' : theoretical === undefined ? '—' : `≈ ${fmtQty(theoretical, unit)}`}
              />
              <Line
                label="Diferencia"
                value={difference === null ? '—' : `${difference > 0 ? '+' : ''}${fmtQty(difference, unit)}${theoretical ? ` (${difference > 0 ? '+' : ''}${whole.format((difference / theoretical) * 100)} %)` : ''}`}
              />
              <Typography sx={{ fontSize: 11, color: MUTED }}>El teórico es del material entero (todos sus proveedores); solo para comparar.</Typography>
              <Box sx={{ pt: 0.75, borderTop: '1px solid rgba(148,163,184,0.16)' }}>
                <Line label="Valor (actual × precio)" value={value === null ? '—' : `${euros.format(value)} €`} strong />
              </Box>
              <Typography title={pricesTitle} sx={{ fontSize: 11, color: MUTED }}>{`Precios de los 3 meses anteriores: ${pricesText}`}</Typography>
            </Stack>
          </Box>
          {failed ? <Alert severity="error">No se pudo guardar. Inténtalo de nuevo.</Alert> : null}
        </Stack>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ px: 2.5, py: 1.5, borderTop: '1px solid rgba(148,163,184,0.16)' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {row.added_by_hand ? (
            <Button color="error" disabled={busy} onClick={onDelete} sx={{ textTransform: 'none', flexShrink: 0 }}>Borrar fila</Button>
          ) : null}
          <Typography sx={{ fontSize: 12, color: MUTED }}>Ámbar = cambiado a mano · azul = sugerencia (solo se escribe con «Usar»)</Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none', height: 42 }}>Cancelar</Button>
          <Button variant="outlined" disabled={busy || invalid || !hasNext} onClick={() => onSave(collect(), true)} sx={{ ...BUTTON, height: 42 }}>
            Guardar y siguiente
          </Button>
          <Button variant="contained" disabled={busy || invalid || !dirty} onClick={() => onSave(collect(), false)} sx={{ ...BUTTON, height: 42 }}>
            Guardar
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}

function Card({
  label,
  hint,
  accent,
  suggestion,
  children,
}: {
  label: string;
  hint: string;
  accent?: boolean;
  suggestion?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack spacing={0.75} sx={{ ...BOX, minWidth: 0, borderColor: accent ? 'rgba(34,211,238,0.35)' : 'rgba(148,163,184,0.16)' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1' }}>{label}</Typography>
      {children}
      {suggestion ?? null}
      <Typography sx={{ fontSize: 11, color: MUTED }}>{hint}</Typography>
    </Stack>
  );
}

function CardInput({
  value,
  amber,
  error,
  disabled,
  label,
  onChange,
}: {
  value: string;
  amber: boolean;
  error: boolean;
  disabled?: boolean;
  label: string;
  onChange: (value: string) => void;
}) {
  return (
    <Box
      component="input"
      inputMode="decimal"
      aria-label={label}
      value={disabled && !value ? '—' : value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      sx={{
        width: '100%',
        height: 40,
        boxSizing: 'border-box',
        px: 1,
        borderRadius: 2,
        border: `1px solid ${error ? '#f87171' : amber ? 'rgba(251,191,36,0.5)' : 'rgba(148,163,184,0.3)'}`,
        bgcolor: '#0b1220',
        color: disabled ? '#64748b' : amber ? AMBER : '#f8fafc',
        font: 'inherit',
        fontSize: 18,
        fontWeight: 800,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      }}
    />
  );
}

function Suggestion({ text, onUse }: { text: string; onUse: () => void }) {
  return (
    <Typography sx={{ fontSize: 12, color: BLUE, fontVariantNumeric: 'tabular-nums' }}>
      {`≈ ${text} · `}
      <Link component="button" type="button" onClick={onUse} sx={{ fontSize: 12 }}>Usar</Link>
    </Typography>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ fontSize: 13 }}>
      <Box component="span" sx={{ color: MUTED }}>{label}</Box>
      <Box component="span" sx={{ fontWeight: strong ? 800 : 400, color: strong ? '#f8fafc' : '#cbd5e1', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
    </Stack>
  );
}
